import { useCallback, useEffect, useRef, useState } from 'react';

// The phone or browser's own speech-to-text (Chrome on Android and desktop). Free, no key, nothing leaves
// the device until the rep saves the note. Where it is missing, the mic button is hidden and reps type.
type Rec = {
  lang: string; continuous: boolean; interimResults: boolean; start(): void; stop(): void;
  onresult: ((e: { resultIndex: number; results: ArrayLike<{ isFinal: boolean; 0: { transcript: string } }> }) => void) | null;
  onend: (() => void) | null; onerror: ((e: { error: string }) => void) | null;
};

function make(): Rec | null {
  const w = window as unknown as { SpeechRecognition?: new () => Rec; webkitSpeechRecognition?: new () => Rec };
  const C = w.SpeechRecognition ?? w.webkitSpeechRecognition;
  return C ? new C() : null;
}

export function useSpeech(onText: (finalText: string) => void) {
  const [supported] = useState(() => typeof window !== 'undefined' && !!make());
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState('');
  const [error, setError] = useState('');
  const rec = useRef<Rec | null>(null);
  const cb = useRef(onText);
  cb.current = onText;

  const stop = useCallback(() => { rec.current?.stop(); }, []);
  const start = useCallback((lang: 'en-IN' | 'hi-IN') => {
    const r = make();
    if (!r) return;
    r.lang = lang; r.continuous = true; r.interimResults = true;
    r.onresult = (e) => {
      let live = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const res = e.results[i];
        if (res.isFinal) cb.current(res[0].transcript.trim());
        else live += res[0].transcript;
      }
      setInterim(live);
    };
    r.onerror = (e) => setError(e.error === 'not-allowed' ? 'Allow the microphone to speak your note.' : `Speech error: ${e.error}`);
    r.onend = () => { setListening(false); setInterim(''); };
    rec.current = r;
    setError('');
    setListening(true);
    r.start();
  }, []);

  useEffect(() => () => rec.current?.stop(), []);
  return { supported, listening, interim, error, start, stop };
}
